import { useState } from "react";
import { Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

interface FavoriteButtonProps {
  artworkId: string;
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
}

export const FavoriteButton = ({ artworkId, size = "icon", className }: FavoriteButtonProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<any>(null);

  // Get current user
  supabase.auth.getSession().then(({ data: { session } }) => {
    setUser(session?.user || null);
  });

  // Check if artwork is favorited
  const { data: isFavorite } = useQuery({
    queryKey: ["favorite", artworkId, user?.id],
    queryFn: async () => {
      if (!user) return false;
      
      const { data, error } = await supabase
        .from("favorites")
        .select("id")
        .eq("user_id", user.id)
        .eq("artwork_id", artworkId)
        .maybeSingle();

      if (error) throw error;
      return !!data;
    },
    enabled: !!user,
  });

  // Toggle favorite mutation
  const toggleFavorite = useMutation({
    mutationFn: async () => {
      if (!user) {
        throw new Error("Du måste vara inloggad");
      }

      if (isFavorite) {
        // Remove favorite
        const { error } = await supabase
          .from("favorites")
          .delete()
          .eq("user_id", user.id)
          .eq("artwork_id", artworkId);

        if (error) throw error;
      } else {
        // Add favorite
        const { error } = await supabase
          .from("favorites")
          .insert({
            user_id: user.id,
            artwork_id: artworkId,
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["favorite", artworkId, user?.id] });
      toast({
        title: isFavorite ? "Borttagen från favoriter" : "Tillagd i favoriter",
        description: isFavorite 
          ? "Konstverket har tagits bort från dina favoriter" 
          : "Konstverket har lagts till i dina favoriter",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Ett fel uppstod",
        description: error.message || "Kunde inte uppdatera favoriter",
        variant: "destructive",
      });
    },
  });

  if (!user) return null;

  return (
    <Button
      size={size}
      variant="ghost"
      aria-label={isFavorite ? "Ta bort från favoriter" : "Lägg till i favoriter"}
      className={cn(
        "hover:scale-110 transition-all duration-300",
        isFavorite && "text-red-500 hover:text-red-600",
        className
      )}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleFavorite.mutate();
      }}
    >
      <Heart 
        className={cn(
          "h-5 w-5",
          isFavorite && "fill-current"
        )} 
      />
    </Button>
  );
};